// src/components/ui/CommentSection.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { fetchComments, addComment, deleteComment } from '@/services/apiService';
import { Textarea } from "@/components/ui/textarea";

/**
 * ┌────────────────────────────────────────────────────────────┐
 * │                    CommentSection.jsx                      │
 * └────────────────────────────────────────────────────────────┘
 * A fully reusable, flexible comment system for submissions.
 *
 * ✅ Fetches and displays comments for a given submission ID
 * ✅ Optional input box and delete button based on props
 * ✅ Displays user info from serializer's nested user object
 * ✅ Handles user display name with proper fallback hierarchy
 * ✅ Renders avatar image when available (profile.avatar_url)
 * ✅ Emits optional callbacks to sync with parent logic
 *
 * CONTRACT:
 * Each comment has:
 *   - id: comment ID
 *   - text: comment body
 *   - user: nested user object {
 *       id, username, email, first_name, last_name,
 *       profile: { name, bio, avatar, avatar_url? }
 *     }
 *   - created_at: ISO timestamp
 *
 * DISPLAY NAME HIERARCHY:
 * 1. user.profile.name (custom display name from profile page) — AUTHORITATIVE
 * 2. user.first_name + user.last_name (fallback)
 * 3. user.username (fallback)
 * 4. 'Anonymous' (default)
 *
 * AVATAR HIERARCHY:
 * 1. user.profile.avatar_url (preferred, fully-qualified URL from serializer)
 * 2. (no avatar) => initials badge
 *
 * 🔁 Used currently in: SubmissionModal.jsx
 */

const CommentSection = ({
  submissionId,
  currentUser,
  allowInput = true,
  allowDelete = true,
  className = '',
  onCommentAdded = () => {},
  onCommentDeleted = () => {},
}) => {
  // 🔄 State management
  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [focused, setFocused] = useState(false);
  // 🔧 FIXED: Track which avatars failed to load so we can show fallback initials
  const [failedAvatars, setFailedAvatars] = useState(new Set());
  const textareaRef = useRef(null);

  // 🔁 Load all comments for this submission
  useEffect(() => {
    let mounted = true;

    setLoading(true);
    fetchComments(submissionId)
      .then((res) => {
        if (!mounted) return;
        setComments(Array.isArray(res) ? res : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch comments:', err);
        if (!mounted) return;
        setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [submissionId]);

  // 🧠 Submit a new comment
  const handleAddComment = async () => {
    if (!commentText.trim()) return;

    try {
      const resp = await addComment({ submission: submissionId, text: commentText });

      // Append new comment to list
      setComments((prev) => [...prev, resp]);
      setCommentText('');

      // Reset textarea height + collapse controls
      setTimeout(() => {
        const textarea = document.querySelector('textarea');
        if (textarea) textarea.style.height = 'auto';
        setFocused(false);
      }, 0);

      onCommentAdded(resp);
    } catch (err) {
      console.error('Comment submission failed:', err);
    }
  };

  // 🧠 Remove a comment
  const handleDeleteComment = async (commentId) => {
    try {
      await deleteComment(commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
      onCommentDeleted(commentId);
    } catch (err) {
      console.error('Comment deletion failed:', err);
    }
  };

  // ✅ Helper: Get user display name with proper precedence
  const getUserDisplayName = (user) => {
    if (!user) return 'Anonymous';

    if (user.profile?.name?.trim()) {
      return user.profile.name.trim();
    }

    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    return fullName || user.username || 'Anonymous';
  };

  // ✅ Helper: Get user initials for fallback badge
  const getUserInitials = (user) => {
    if (!user) return 'A';

    if (user.profile?.name?.trim()) {
      const parts = user.profile.name.trim().split(' ');
      const initials = parts.map(p => p.charAt(0).toUpperCase()).join('');
      return initials || 'A';
    }

    const firstInitial = user.first_name?.charAt(0) || '';
    const lastInitial = user.last_name?.charAt(0) || user.username?.charAt(0) || '';

    return (firstInitial + lastInitial).toUpperCase() || 'A';
  };

  // ✅ Helper: Get avatar URL if present
  // This assumes your serializer returns a fully-usable URL at profile.avatar_url
  const getUserAvatarUrl = (user) => {
    const url = user?.profile?.avatar_url;
    return typeof url === 'string' && url.trim() ? url.trim() : null;
  };

  // ✅ Helper: Check if current user can delete a comment
  const canDeleteComment = (comment) => {
    if (!allowDelete || !currentUser) return false;

    const commentUserId = comment.user?.id;
    const currentUserId = currentUser.id;

    return commentUserId === currentUserId;
  };

  // 🔧 FIXED: Handle avatar load failure by tracking comment ID instead of DOM manipulation
  const handleAvatarError = (commentId) => {
    setFailedAvatars((prev) => new Set([...prev, commentId]));
  };

  return (
    <div className={`space-y-3 ${className}`}>
      <h4 className="font-semibold text-[15px] text-swan">{comments.length} Comments</h4>

      {/* 🔓 Optional comment input field */}
      {allowInput && (
        <div>
         <Textarea
            ref={textareaRef}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Add a comment..."
            rows={1}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onInput={(e) => {
              e.target.style.height = 'auto';
              e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            style={{
              border: 'none',
              outline: 'none',
              boxShadow: 'none',
            }}
            className="w-full resize-none bg-transparent text-swan placeholder:text-field-grey text-sm focus:outline-none min-h-0 py-0 box-border"
          />

          {(focused || commentText.length > 0) && (
            <div className="flex justify-end gap-2 mt-2 py-2">
              <Button
                variant="negative"
                size="sm"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setCommentText('');
                  setFocused(false);
                  if (textareaRef.current) {
                    textareaRef.current.style.height = 'auto';
                    textareaRef.current.rows = 1;
                  }
                }}
              >
                Cancel
              </Button>

              <Button
                variant="primary"
                size="sm"
                onClick={handleAddComment}
                disabled={!commentText.trim()}
              >
                Comment
              </Button>
            </div>
          )}
        </div>
      )}

      {/* 🧠 Display loading or actual comments */}
      {loading ? (
        <p className="text-sm text-field-grey italic">Loading...</p>
      ) : (
        comments.map((comment) => {
          const user = comment.user || {};
          const displayName = getUserDisplayName(user);
          const initials = getUserInitials(user);
          const avatarUrl = getUserAvatarUrl(user);
          // 🔧 FIXED: Check if this comment's avatar has failed to load
          const avatarFailed = failedAvatars.has(comment.id);
          const showAvatarImage = avatarUrl && !avatarFailed;

          return (
            <div key={comment.id} className="flex justify-between items-center gap-3">
              <div className="flex gap-2 items-center flex-1">
                {/* ✅ Avatar image if available and hasn't failed, otherwise initials badge */}
                {showAvatarImage ? (
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="w-6 h-6 rounded-full object-cover border border-field-grey flex-shrink-0"
                    onError={() => handleAvatarError(comment.id)}
                    loading="lazy"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-mikado-yellow text-black flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {initials}
                  </div>
                )}

                {/* Comment content */}
                <div className="flex-1">
                  <p className="text-sm text-swan font-poppins whitespace-pre-line">
                    <span className="text-mikado-yellow font-semibold">
                      {displayName}:
                    </span>{' '}
                    {comment.text}
                  </p>
                </div>
              </div>

              {/* ✅ Delete button (only for comment author) — now a proper <button> element */}
              {canDeleteComment(comment) && (
                <button
                  className="text-xs text-imperial-red flex-shrink-0 hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-imperial-red focus:ring-offset-2 focus:ring-offset-stone-brown rounded px-1 py-0.5"
                  onClick={() => handleDeleteComment(comment.id)}
                  title="Delete comment"
                  type="button"
                >
                  Delete
                </button>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default CommentSection;